/**
 * lib/reports/weekWindow.ts — Rolling Weekly Report Window Resolver
 * ====================================================================
 * Resolves the "last completed Monday–Sunday" window relative to `now`,
 * plus the week immediately before it (for week-over-week comparison).
 *
 * Deliberately independent of src/config/dateOverride.ts — that file is a
 * temporary demo shim ("no live data for today") and must NOT influence
 * which real calendar week this report resolves to. Pass a real Date in
 * (or default to `new Date()`).
 *
 * Example (today = Wed 29 Jul 2026):
 *   reportWeek      = Mon 20 Jul 00:00:00 → Sun 26 Jul 23:59:59.999
 *   comparisonWeek  = Mon 13 Jul 00:00:00 → Sun 19 Jul 23:59:59.999
 *
 * Once the calendar rolls to Mon 3 Aug, calling this again automatically
 * shifts to Mon 27 Jul – Sun 2 Aug. No manual date bookkeeping required.
 */

export interface DateWindow {
  from: Date;
  to:   Date;
  /** Human label, e.g. "20 Jul – 26 Jul 2026". */
  label: string;
}

function startOfMonday(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  // getDay(): 0=Sun..6=Sat. Days since most recent Monday:
  const dow = copy.getDay();
  const daysSinceMonday = (dow + 6) % 7; // Mon->0, Tue->1, ..., Sun->6
  copy.setDate(copy.getDate() - daysSinceMonday);
  return copy;
}

function addDays(d: Date, days: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function endOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

function fmt(d: Date): string {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function windowLabel(from: Date, to: Date): string {
  return `${fmt(from)} – ${fmt(to)}, ${to.getFullYear()}`;
}

export interface WeeklyReportWindows {
  reportWeek:     DateWindow;
  comparisonWeek: DateWindow;
}

/**
 * @param now  Real current time. Defaults to `new Date()`. Pass an explicit
 *             value in tests; never wire this to the dateOverride.ts shim.
 */
export function resolveWeeklyReportWindows(now: Date = new Date()): WeeklyReportWindows {
  const thisMonday = startOfMonday(now);

  const lastMonday = addDays(thisMonday, -7);
  const lastSunday = endOfDay(addDays(thisMonday, -1));

  const priorMonday = addDays(lastMonday, -7);
  const priorSunday = endOfDay(addDays(lastMonday, -1));

  return {
    reportWeek: {
      from: lastMonday,
      to: lastSunday,
      label: windowLabel(lastMonday, lastSunday),
    },
    comparisonWeek: {
      from: priorMonday,
      to: priorSunday,
      label: windowLabel(priorMonday, priorSunday),
    },
  };
}
